import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { stageRig } from '../lighting/stage-light-rig.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';

// Shared-rig contract (see the framing addendum). intensityScale 0.6: at
// full strength the beam cones flatten this scene, since the tubes are now
// close to the camera and fill the frame. zones 'floor' keeps every head
// aiming at the floor and the tube volume, never down the camera->artwork
// axis.
const RIG = 'required';
const RIG_OWNER = 'kineticrig2';
const RIG_INTENSITY_SCALE = 0.6;
const RIG_ZONES = 'floor';
// A 'floor' owner also gets the rig's spot falloff relaxed (decay 2.4 -> 1.1,
// see StageLightRig.setOwnerOptions), which alone brings the light within
// ~1/10 rather than ~1/170 at floor distance. This is the remaining trim on
// top of that. Scales spot.intensity only, never the cone meshes.
// 5, not 3: intensityScale now dims spot.intensity too (it did not when this
// was first tuned), so 0.6 * 5 lands on the same 3x this visualizer's floor
// pools were calibrated against. Owners at intensityScale 1 are unaffected.
const RIG_FLOOR_LIGHT_BOOST = 5;
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'kineticrig2',
  family: 'stage',
  energyLevel: 0.80,
  bpmMin: 108,
  bpmMax: 178,
  movement: 0.85,
  danceability: 0.80,
  tonality: 0.70,
  enabled: true
};

// KineticRig2: the same rig as kinetic-rig.js, turned through a right angle -
// a suspended matrix of HORIZONTAL neon battens instead of vertical tubes.
//
// Everything structural is inherited deliberately: one draw call, all the
// animation in the vertex shader off scalar uniforms, no per-frame attribute
// upload, shockwaves rolling physically through the matrix on every beat. What
// changes is what the orientation forces to change, and it is worth being
// precise about that because three things follow from it and nothing else does:
//
//  - THE GRID IS (LEVEL, DEPTH), not (column, depth). A vertical tube occupies
//    one x; a horizontal batten spans the whole width, so the axis it can be
//    indexed on is the one it does NOT lie along. Levels stack up the frame,
//    rows recede.
//  - THE WINCH BECOMES THE BATTEN'S OWN HEIGHT. There is no cable to pay out
//    along the tube's length any more, so the height field drives the bar's y
//    directly and the field is read over (level, depth).
//  - THE BILLBOARD ROLLS INSTEAD OF YAWING. The original yaws around Y to face
//    the camera while staying vertical; a batten has to roll around its OWN
//    axis - world X - to face the camera while staying horizontal. Seen from
//    under the rig a batten that does not roll is edge-on and vanishes.
//
// The vault of the original - columns near x=0 riding higher to open a gap
// above the artwork - has no meaning when every bar crosses the whole frame.
// Its purpose survives as a BAND: battens near the cover's own height are
// pushed away from it, above and below, opening a horizontal corridor across
// the artwork instead of a vault above it.

// Across the batten, not along it: the quad's short axis carries the halo and
// the hot core exactly as the tube's width did.
const BAR_TOTAL_WIDTH = 0.17;
const BAR_CORE_FRACTION = 0.1 / 0.3;
// A batten never touches the floor, so the travel is centred on the look-at
// height rather than hanging from a ceiling anchor.
const BAR_Y_MIN = 0.45;
const BAR_Y_MAX = 9.2;

const CAMERA_POS = [0, 3.2, 6.5];
const CAMERA_LOOK = [0, 3.4, -3.0];

// z(row) = ROW_Z_NEAR - ROW_Z_SPAN * (row/(rows-1))^ROW_Z_POWER
const ROW_Z_NEAR = 2.5;
const ROW_Z_SPAN = 22.5;
const ROW_Z_POWER = 1.6;
// The middle row overflows the visible width by this factor (1.4 == 40% off
// each side), per the addendum's framing rule.
// 0.62, where this was 1.4. At 1.4 every batten was 140% of the visible width
// and was therefore cut by BOTH edges, so not one of them had an end on screen
// - and a bar with no ends is not a bar, it is a scan line. The frame read as a
// striped pattern rather than as a rig of suspended battens, which is the thing
// the piece is about. At 0.62 a typical batten covers under two thirds of the
// width and its ends are in the picture.
const SPAN_X_OVERFLOW = 0.62;
// AND THEY MUST NOT ALL BE THE SAME BAR. Equal lengths all centred on x = 0
// line their ends up into two vertical columns, which reads as a solid block
// with a hole either side - worse than the stripes. Each batten gets its own
// length and its own offset along its axis, drawn once at build time and
// carried on the instance, so the ends stagger the way a real rig's do.
const BAR_LEN_VARIATION = [0.55, 1.20];
const BAR_X_STAGGER = 0.26;     // of the visible width, either way

const HEIGHT_AMPLITUDE = 1.15; // world units of vertical travel at full amplitude
const LENGTH_MODULATION = 0.15; // +/-15% length from the same field, phase-shifted
// The band that opens across the artwork: battens within BAND_WIDTH of the
// cover's height are pushed away from it, up or down whichever side they are.
const BAND_PUSH = 1.25;
const BAND_WIDTH = 1.9;
const BAND_CENTRE_Y = 4.30;
// THE STACK HAS TO FIT ABOVE THE FLOOR, and that is what sizes it - not the
// frame. At a full frame height centred on the look-at point the lowest levels
// land at y = -2.2, below the deck, where the clamp piles every one of them on
// its floor value and they add up into a bar of white across the bottom of the
// picture. 0.80 of the visible height, centred at 4.30, puts the stack in
// y = [0.64, 7.96]: clear of the floor, and leaving it and its light pools to
// fill the bottom of the frame as they do in the original.
const SPAN_Y_OVERFLOW = 0.80;
const RING_LIFT = 0.9; // upward impulse a shockwave adds
const ATMOSPHERIC_DENSITY = 0.045; // explicit exp(-dist * k) haze - FogExp2 does not reach custom ShaderMaterials

const RING_SLOTS = 4;
const RING_DECAY = 2.2;
const RING_STRENGTH_DOWNBEAT = 1.0;
const RING_STRENGTH_OFFBEAT = 0.55;
const BEATS_PER_BAR = 4;

const REFERENCE_BPM = 120;
const TEMPO_MULTIPLIER_MIN = 0.6;
const TEMPO_MULTIPLIER_MAX = 1.8;
const NO_TEMPO_RATE = 0.8;

const BEAT_PULSE_AMOUNT = 0.45;
const BEAT_PULSE_DECAY = 9;

const PALETTE_LERP_S = 0.6;
const FALLBACK_COLOR_A = 0xff2fb0;
const FALLBACK_COLOR_B = 0x2fd8ff;
const ENERGY_THRESHOLD = 0.6;

const REFLECTION_OPACITY = 0.22;
const REFLECTION_DESAT = 0.25;
// The higher a tube hangs, the weaker its reflection - without this the
// mirrored copy reads as a second, equally solid rig under the floor.
const REFLECTION_FALLOFF = 0.16;

const FLOOR_SIZE = 60;
// The addendum specifies 0x050507, but that is ~0.0002 in linear space once
// sRGB-decoded: a surface reflecting 0.02% of the light it receives cannot
// show a light pool at any intensity, so "0x050507" and "montrer les flaques
// des SpotLight" are mutually exclusive. Kept as dark as a black stage floor
// reads while still being able to catch light at all.
const FLOOR_COLOR = 0x16161c;

// FEWER, AND FOR TWO SEPARATE REASONS. A frame is shorter than it is wide, so
// nineteen battens stacked up it would close into a solid sheet - that decides
// the shape. And a batten crosses the WHOLE frame where a vertical tube
// occupies one narrow column, so it costs far more fill: measured on the Pi at
// 0.43 ms a bar against the original's 0.19 ms a tube, 2.3 times each. Matching
// the original's frame time therefore means roughly half its count, not the
// same one.
const TIER_LAYOUT = {
  0: { cols: 6, rows: 4, reflection: false, radial: false },
  1: { cols: 8, rows: 6, reflection: false, radial: true },
  2: { cols: 12, rows: 8, reflection: true, radial: true }
};
// Tier 1 by default: both shipping targets (Pi 4 / Tizen TV) are fill-rate
// bound and the reflection roughly doubles covered pixels. Tier 2 is an
// explicit opt-in - new KineticRigVisualizer({ qualityTier: 2 }).
const DEFAULT_TIER = 1;


const vertexShader = `
  precision highp float;

  uniform float uTime;
  uniform float uAmplitude;
  uniform float uEnergy;
  uniform float uRingSpeed;
  uniform float uSpanX;      // batten length - the bar runs the frame and past it
  uniform float uSpanY;      // vertical extent the stack of levels covers
  uniform vec4 uRings[${RING_SLOTS}];
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  uniform float uPulse;
  uniform float uMirror;

  // (levelNormalised -0.5..0.5, worldZ, phase, rowNormalised 0..1)
  attribute vec4 iTube;
  // (lengthFactor, offset along the batten's own axis, both as fractions of
  // the span) - fixed per batten, written once, never per frame.
  attribute vec2 iVar;

  varying vec3 vColor;
  varying float vGlow;
  varying float vU;
  varying float vV;
  varying float vFog;
  varying float vSafeAtt;

  ${SAFE_ZONE_VERTEX_CHUNK}

  float heightField(vec2 g, float t) {
    float h = sin(g.x * 0.28 + t * 0.9) * 0.6
            + sin(g.y * 0.6 + t * 0.7 + 1.7) * 0.4;
    #ifdef USE_RADIAL
      h += sin(length(g) * 0.45 - t * 0.5) * 0.25;
    #endif
    h += uEnergy * sin(g.x * 0.35 + g.y * 0.5 + t * 1.3) * 0.3;
    return h;
  }

  // One shockwave slot. Called RING_SLOTS times explicitly rather than in a
  // loop - guaranteed unrolled on the oldest WebGL1 drivers we still target.
  float ringHit(vec4 ring, vec2 g, float t) {
    float age = t - ring.z;
    float live = step(0.0001, ring.w) * step(0.0, age);
    float radius = age * uRingSpeed;
    float d = abs(distance(g, ring.xy) - radius);
    float hit = ring.w * exp(-age * ${RING_DECAY.toFixed(1)}) * (1.0 - smoothstep(0.0, 0.9, d));
    return hit * live;
  }

  void main() {
    float t = uTime;
    // The field is read over (LEVEL, DEPTH). Both spans are uniforms, so a
    // resize re-lays the grid without ever touching a vertex buffer.
    vec2 g = vec2(iTube.x * uSpanY, iTube.y);

    float field = heightField(g, t + iTube.z * 0.35);
    float stretch = 1.0 + heightField(g, t + 2.3 + iTube.z * 0.35) * ${LENGTH_MODULATION.toFixed(3)};

    float rings = ringHit(uRings[0], g, t)
                + ringHit(uRings[1], g, t)
                + ringHit(uRings[2], g, t)
                + ringHit(uRings[3], g, t);

    // THE BAND. Every batten crosses the whole frame, so there is no vault to
    // open above the artwork; instead the levels that would sit across it are
    // pushed AWAY from its height, up or down whichever side they start.
    // sign() rather than a branch, and exp(-a*a) rather than pow, to keep both
    // a multiply.
    float ay = g.x / ${BAND_WIDTH.toFixed(2)};
    float band = ${BAND_PUSH.toFixed(2)} * sign(g.x) * exp(-ay * ay);

    float barY = clamp(
      ${BAND_CENTRE_Y.toFixed(2)} + g.x
        + field * uAmplitude * ${HEIGHT_AMPLITUDE.toFixed(2)} + band + rings * ${RING_LIFT.toFixed(2)},
      ${BAR_Y_MIN.toFixed(2)}, ${BAR_Y_MAX.toFixed(2)});

    float len = uSpanX * stretch * iVar.x;
    // uv.y 0 = one end of the batten, 1 = the other, and each one is shifted
    // along its own axis so the ends do not line up into two columns.
    float alongX = (uv.y - 0.5) * len + iVar.y * uSpanX;

    // BILLBOARD BY ROLL, around the batten's own axis. The original yaws around
    // Y to stay vertical; a horizontal bar has to turn about world X instead,
    // or it is seen edge-on from under the rig and disappears. cameraPosition
    // is a ShaderMaterial built-in, so following the shared Ken Burns drift
    // costs no uniform write of our own.
    float my = barY * uMirror;
    vec3 toCam = vec3(0.0, cameraPosition.y - my, cameraPosition.z - g.y);
    float l = length(toCam);
    vec3 fwd = l > 0.0001 ? toCam / l : vec3(0.0, 0.0, 1.0);
    vec3 across = normalize(cross(vec3(1.0, 0.0, 0.0), fwd));

    vec3 worldPos = vec3(alongX, my, g.y)
                  + across * (position.x * ${BAR_TOTAL_WIDTH.toFixed(3)});

    vec4 mvPosition = modelViewMatrix * vec4(worldPos, 1.0);
    gl_Position = projectionMatrix * mvPosition;

    vU = uv.x * 2.0 - 1.0;
    vV = uv.y;
    // Explicit atmospheric falloff on view distance.
    vFog = exp(-length(mvPosition.xyz) * ${ATMOSPHERIC_DENSITY.toFixed(3)});

    // Front-to-back colour gradient across the depth rows, each row drifting
    // slowly (~20s) with its own offset so the matrix is never one flat sheet.
    float mixT = clamp(iTube.w + sin(uTime * 0.314 + iTube.w * 2.4) * 0.25, 0.0, 1.0);
    vColor = mix(uColorA, uColorB, mixT);
    // Shockwaves brighten as well as lift - that is what makes a wave read
    // as struck rather than merely moved.
    vGlow = uPulse * (1.0 + rings * 1.35);

    // Mirrored copies fade with height above the floor.
    if (uMirror < 0.0) vGlow *= exp(-barY * ${REFLECTION_FALLOFF.toFixed(3)});

    vSafeAtt = safeZoneAttenuation(gl_Position);
  }
`;

const fragmentShader = `
  precision mediump float;

  uniform float uOpacity;
  uniform float uDesat;

  varying vec3 vColor;
  varying float vGlow;
  varying float vU;
  varying float vV;
  varying float vFog;
  varying float vSafeAtt;

  void main() {
    float au = abs(vU);
    // Hot core over the central 0.10 of the 0.30-wide quad, colour halo
    // falling off to the edges. No discard: on tile-based GPUs a discard in
    // a shader with this much coverage kills early-Z for the whole tile, so
    // the tube shape lives entirely in the alpha ramp.
    float core = 1.0 - smoothstep(0.0, ${BAR_CORE_FRACTION.toFixed(4)}, au);
    float halo = 1.0 - au;
    halo *= halo;

    // Only 0.35 toward white: the album's colour has to stay franchement
    // recognizable, which the previous 0.75 destroyed.
    vec3 col = mix(vColor, vec3(1.0), core * 0.35) * vGlow;
    col = mix(vec3(dot(col, vec3(0.299, 0.587, 0.114))), col, 1.0 - uDesat);
    // Peak luminance under 1.0 so bloom (threshold 0.78) catches the cores
    // without blowing to flat white under ACES.
    col = min(col, vec3(0.92));

    // The ends are now the LEFT and RIGHT of the frame, and both are off it -
    // so the fade is kept narrow enough that a cut batten never shows a taper
    // where the screen edge should simply crop it.
    float ends = smoothstep(0.0, 0.015, vV) * (1.0 - smoothstep(0.985, 1.0, vV));
    // 0.40/0.34, not the original's 0.75/0.60. A vertical tube covers a narrow
    // column and its neighbours sit beside it; a horizontal batten crosses the
    // whole frame, so every level of every depth row lays light over the same
    // screen rows and ninety-six of them add up. Same look per bar, a third of
    // the accumulated fill.
    float alpha = (halo * 0.40 + core * 0.34) * ends * uOpacity * vFog * vSafeAtt;

    gl_FragColor = vec4(col, alpha);
  }
`;

export class KineticRig2Visualizer extends Visualizer {
  // options.qualityTier (0/1/2, default 1) - see TIER_LAYOUT/DEFAULT_TIER.
  //
  // NOTE ON SIGNATURE: the brief specified `constructor(scene, camera,
  // options)`, but SceneManager (and every other visualizer) uses
  // `init(scene, camera)` with a no-arg-or-options constructor. Those two
  // requirements conflict; the codebase's actual contract wins, otherwise
  // SceneManager could not drive this class at all.
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    camera.position.set(CAMERA_POS[0], CAMERA_POS[1], CAMERA_POS[2]);
    camera.lookAt(CAMERA_LOOK[0], CAMERA_LOOK[1], CAMERA_LOOK[2]);

    this._rig = stageRig.acquire(RIG_OWNER, {
      scene: scene,
      intensityScale: RIG_INTENSITY_SCALE,
      zones: RIG_ZONES,
      floorLightBoost: RIG_FLOOR_LIGHT_BOOST
    });

    // A real lit floor, not a black void: MeshStandardMaterial so the rig's
    // SpotLights actually draw pools of light on it, which is what fills the
    // bottom third of the frame.
    const floorGeometry = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE);
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: FLOOR_COLOR,
      roughness: 0.78,
      metalness: 0.0
    });
    this._floor = new THREE.Mesh(floorGeometry, floorMaterial);
    this._floor.rotation.x = -Math.PI / 2;
    this._floor.receiveShadow = false;
    scene.add(this._floor);

    this._buildTubes(TIER_LAYOUT[this._tier]);

    this._time = 0;
    this._lastBeatPhase = null;
    // Phase 5 validation - see director.bandPeak (track-analysis.js's
    // per-band envelopes). null when no analysis timeline is available.
    this._lastBandPeakLow = null;
    this._beatCount = 0;
    this._ringCursor = 0;
    this._perimeterAngle = 0;
    this._lastPaletteRef = null;
    this._lastAspect = null;
    this._colorFrom = [new THREE.Color(FALLBACK_COLOR_A), new THREE.Color(FALLBACK_COLOR_B)];
    this._colorTo = [new THREE.Color(FALLBACK_COLOR_A), new THREE.Color(FALLBACK_COLOR_B)];
    this._colorLerp = 1;
    this._uniforms.uColorA.value.copy(this._colorTo[0]);
    this._uniforms.uColorB.value.copy(this._colorTo[1]);
    this._updateSpanX();
  }

  // Distance from the camera to the field's median row - the depth the
  // column span is sized against.
  _midRowDistance() {
    return CAMERA_POS[2] - this._midRowZ;
  }

  // BOTH spans, from the camera, at the median row's depth. The batten length
  // is a fraction of the width so the bars have visible ends; the stack of
  // levels still overflows the height so the top and bottom rows are cut. Recomputed whenever the aspect changes and pushed as uniforms,
  // so a resize never rebuilds a vertex buffer.
  _updateSpanX() {
    const vFovRad = THREE.MathUtils.degToRad(this._camera.fov);
    const visibleHeight = 2 * this._midRowDistance() * Math.tan(vFovRad / 2);
    const visibleWidth = visibleHeight * this._camera.aspect;
    this._uniforms.uSpanX.value = visibleWidth * SPAN_X_OVERFLOW;
    this._uniforms.uSpanY.value = visibleHeight * SPAN_Y_OVERFLOW;
    this._lastAspect = this._camera.aspect;
  }

  _buildTubes(layout) {
    const cols = layout.cols;
    const rows = layout.rows;
    const count = cols * rows;
    this._tubeCount = count;

    // (levelNormalised, worldZ, phase, rowNormalised) - one vec4 instead of
    // four separate attributes.
    const tubes = new Float32Array(count * 4);
    // (lengthFactor, axial offset) - see BAR_LEN_VARIATION.
    const vars = new Float32Array(count * 2);

    const rowZ = [];
    for (let row = 0; row < rows; row++) {
      const f = rows > 1 ? row / (rows - 1) : 0;
      rowZ.push(ROW_Z_NEAR - ROW_Z_SPAN * Math.pow(f, ROW_Z_POWER));
    }
    // Median row depth, for the span calculation above.
    this._midRowZ = rowZ[Math.floor((rows - 1) / 2)];
    this._nearRowZ = rowZ[0];
    this._farRowZ = rowZ[rows - 1];

    let i = 0;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        tubes[i * 4] = cols > 1 ? col / (cols - 1) - 0.5 : 0;
        tubes[i * 4 + 1] = rowZ[row];
        tubes[i * 4 + 2] = Math.random() * Math.PI * 2;
        tubes[i * 4 + 3] = rows > 1 ? row / (rows - 1) : 0;
        vars[i * 2] = BAR_LEN_VARIATION[0]
          + Math.random() * (BAR_LEN_VARIATION[1] - BAR_LEN_VARIATION[0]);
        vars[i * 2 + 1] = (Math.random() - 0.5) * 2 * BAR_X_STAGGER;
        i++;
      }
    }

    // Depth extent the shockwaves travel across, in world Z - the field
    // always spans exactly ROW_Z_SPAN from the near row to the far one.
    this._ringRadiusRowSpan = ROW_Z_SPAN;

    const positions = new Float32Array([-0.5, 0, 0, 0.5, 0, 0, 0.5, 1, 0, -0.5, 1, 0]);
    const uvs = new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]);

    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    geometry.setAttribute('iTube', new THREE.InstancedBufferAttribute(tubes, 4));
    geometry.setAttribute('iVar', new THREE.InstancedBufferAttribute(vars, 2));
    geometry.instanceCount = count;
    // Placement is entirely shader-side, so the mesh's own bounds mean
    // nothing to the frustum culler.
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 4, -8), 80);
    this._geometry = geometry;

    const rings = [];
    for (let r = 0; r < RING_SLOTS; r++) rings.push(new THREE.Vector4(0, 0, 0, 0));

    // One set of holder objects shared by both materials, so the reflection
    // tracks the main pass with no extra per-frame writes. safeZoneUniforms
    // are shared app-wide and written once per resize by scene-manager.
    this._uniforms = Object.assign({}, safeZoneUniforms, {
      uTime: { value: 0 },
      uAmplitude: { value: 1 },
      uEnergy: { value: 0 },
      uRingSpeed: { value: 0 },
      uSpanX: { value: 30 },
      uSpanY: { value: 8 },
      uRings: { value: rings },
      uColorA: { value: new THREE.Color(FALLBACK_COLOR_A) },
      uColorB: { value: new THREE.Color(FALLBACK_COLOR_B) },
      uPulse: { value: 1 }
    });

    const defines = layout.radial ? { USE_RADIAL: '' } : {};
    const common = {
      defines: defines,
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    };

    this._material = new THREE.ShaderMaterial(Object.assign({}, common, {
      uniforms: Object.assign({}, this._uniforms, {
        uMirror: { value: 1 },
        uOpacity: { value: 1 },
        uDesat: { value: 0 }
      })
    }));
    this._tubes = new THREE.Mesh(geometry, this._material);
    this._scene.add(this._tubes);

    // Tier 2 only: the same instanced geometry mirrored under the floor,
    // reading the very same animated uniforms. uMirror flips Y in-shader
    // rather than via a negative object scale, which would also flip the
    // billboard basis and the winding.
    this._reflectionMaterial = null;
    this._reflection = null;
    if (layout.reflection) {
      this._reflectionMaterial = new THREE.ShaderMaterial(Object.assign({}, common, {
        uniforms: Object.assign({}, this._uniforms, {
          uMirror: { value: -1 },
          uOpacity: { value: REFLECTION_OPACITY },
          uDesat: { value: REFLECTION_DESAT }
        }),
        // The mirrored copy lives BELOW the floor plane, so an opaque floor
        // depth-rejects every one of its pixels and the reflection never
        // appears at all. Drawing it without a depth test, ordered after the
        // floor, additively lays it over the floor instead - which is also
        // physically what a glossy floor reflection looks like.
        depthTest: false
      }));
      this._reflection = new THREE.Mesh(geometry, this._reflectionMaterial);
      this._reflection.renderOrder = 1;
      this._scene.add(this._reflection);
    }
  }

  _applyPalette(profile) {
    const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
    const hasVivid = vivid && vivid.length > 0;
    const a = hasVivid ? vivid[0] : FALLBACK_COLOR_A;
    const b = hasVivid ? vivid[Math.min(2, vivid.length - 1)] : FALLBACK_COLOR_B;
    this._colorFrom[0].copy(this._uniforms.uColorA.value);
    this._colorFrom[1].copy(this._uniforms.uColorB.value);
    this._colorTo[0].set(a);
    this._colorTo[1].set(b);
    this._colorLerp = 0;
  }

  // Rising edge of beatPhase (it ramps 0->1 then wraps). Every 4th beat is
  // the downbeat and fires a stronger wave from the centre; the others fire
  // from a slowly rotating point out on the perimeter. (The brief mentions a
  // Director-provided barPhase; no such field exists on the profile in this
  // project, so the local count is the implementation, not a fallback.)
  _emitRing() {
    const isDownbeat = this._beatCount % BEATS_PER_BAR === 0;
    let ox = 0;
    let oz = this._midRowZ;
    if (!isDownbeat) {
      this._perimeterAngle += 2.39996; // golden angle - successive off-beats land well apart
      // The waves travel in (level, depth), so the perimeter they are thrown
      // from is sized on the LEVEL span, not the batten length.
      const rx = this._uniforms.uSpanY.value * 0.35;
      const rz = this._ringRadiusRowSpan * 0.35;
      ox = Math.cos(this._perimeterAngle) * rx;
      oz = this._midRowZ + Math.sin(this._perimeterAngle) * rz;
    }
    const slot = this._uniforms.uRings.value[this._ringCursor];
    slot.set(ox, oz, this._time, isDownbeat ? RING_STRENGTH_DOWNBEAT : RING_STRENGTH_OFFBEAT);
    this._ringCursor = (this._ringCursor + 1) % RING_SLOTS;
    this._beatCount++;
  }

  update(deltaTime, profile) {
    stageRig.update(deltaTime, profile, RIG_OWNER);

    if (this._camera.aspect !== this._lastAspect) this._updateSpanX();

    const paused = profile.playbackState !== 'PLAYING';
    const bpm = profile.trackFeatures && profile.trackFeatures.bpm;
    const tempoMul = bpm
      ? THREE.MathUtils.clamp(bpm / REFERENCE_BPM, TEMPO_MULTIPLIER_MIN, TEMPO_MULTIPLIER_MAX)
      : NO_TEMPO_RATE;

    // Freezing _time freezes the height field, the stretch, and crucially
    // the age of any wave mid-flight, since ages are (uTime - startTime) in
    // this same clock. A wave paused halfway across stays halfway across.
    if (!paused) this._time += deltaTime * tempoMul;
    this._uniforms.uTime.value = this._time;

    // One wave crosses the field in exactly one beat period. _time runs at
    // tempoMul x wall-clock, so the beat period is expressed in those same
    // scaled units for the two to line up on screen.
    const maxRingRadius = Math.sqrt(
      Math.pow(this._uniforms.uSpanY.value * 0.5, 2) + Math.pow(this._ringRadiusRowSpan * 0.5, 2)
    );
    this._uniforms.uRingSpeed.value = bpm ? maxRingRadius / (tempoMul * (60 / bpm)) : 0;

    // Phase 5 validation - when a real analysis timeline is available, the
    // shock wave tracks the actual kick (director.bandPeak.low's own
    // rising edge, same exact-equality pattern laser-cathedral/
    // kaleido-drift/type-slam already use for accentPhrase) instead of a
    // plain per-beat metronome tick. Falls back to the original beatPhase-
    // wrap behavior unchanged whenever there's no analysis.
    const hasAnalysisBands = !!profile.analysisTimeline;
    if (hasAnalysisBands) {
      const bandPeakLow = profile.director.bandPeak.low;
      if (!paused && bandPeakLow === 1 && this._lastBandPeakLow !== 1) this._emitRing();
      this._lastBandPeakLow = bandPeakLow;
      this._lastBeatPhase = null;
    } else if (!paused && profile.beatPhase != null) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) this._emitRing();
      this._lastBeatPhase = profile.beatPhase;
      this._lastBandPeakLow = null;
    } else if (profile.beatPhase == null) {
      // No tempo: no waves, and slots are cleared so nothing stays frozen
      // mid-flight from a previous track that did have BPM.
      this._lastBeatPhase = null;
      this._lastBandPeakLow = null;
      const ringValues = this._uniforms.uRings.value;
      for (let r = 0; r < RING_SLOTS; r++) ringValues[r].w = 0;
    }

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile);
    }
    if (this._colorLerp < 1) {
      this._colorLerp = Math.min(1, this._colorLerp + deltaTime / PALETTE_LERP_S);
      this._uniforms.uColorA.value.copy(this._colorFrom[0]).lerp(this._colorTo[0], this._colorLerp);
      this._uniforms.uColorB.value.copy(this._colorFrom[1]).lerp(this._colorTo[1], this._colorLerp);
    }

    const energy = profile.trackFeatures && profile.trackFeatures.energy;
    this._uniforms.uEnergy.value =
      energy != null && energy / 100 > ENERGY_THRESHOLD
        ? (energy / 100 - ENERGY_THRESHOLD) / (1 - ENERGY_THRESHOLD)
        : 0;

    const intensity = profile.director.intensity;
    this._uniforms.uAmplitude.value = 0.35 + intensity * 0.65;
    this._uniforms.uPulse.value = profile.director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);
  }

  getLightRig() {
    return stageRig.get();
  }

  dispose() {
    stageRig.release(RIG_OWNER);
    this._rig = null;
    this._scene.remove(this._floor);
    this._floor.geometry.dispose();
    this._floor.material.dispose();
    this._scene.remove(this._tubes);
    if (this._reflection) this._scene.remove(this._reflection);
    // One shared geometry across both meshes - disposed once.
    this._geometry.dispose();
    this._material.dispose();
    if (this._reflectionMaterial) this._reflectionMaterial.dispose();
  }
}
